/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  ChangeEvent,
  Dispatch,
  HTMLAttributes,
  SetStateAction,
  useEffect,
  useState,
  useCallback
} from 'react';
import {
  ModalOverride,
  ModalContent,
  CheckBoxContainer,
  TextfieldWrapper,
  InputWrapper,
  ColumnsDataContainer,
  TypeNumberLinesContainer,
  Title
} from './styles';
import {
  ECodeType,
  ESpreadsheetField,
  IOrderEntryPreference
} from '../../../../../../store/ducks/user/types';
import { Chips } from '@gerdau/hefesto-mais-web';
import { Checkbox, Modal, TextField } from '@gerdau/hefesto';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';

interface SpreadsheetConfigModalProps {
  canShowModal: boolean;
  orderEntryPreference: IOrderEntryPreference;
  onConfirmClick(
    orderEntryPreference: IOrderEntryPreference,
    hasConfigChanged: boolean
  ): void;
  setCanShowModal: Dispatch<SetStateAction<boolean>>;
}

interface ColumnsDataSequenceProps {
  inputsValues: Record<string, string>;
  columns: ColumnDef[];
  onInputChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => void;
}

interface ColumnDef {
  idx: string;
  title: string;
  type: string;
  helperText?: string;
  maxLength?: number;
}

const chipsData = [
  {
    id: ECodeType.SKU,
    label: 'Código de material da Gerdau'
  },
  {
    id: ECodeType.CLIENT_SKU,
    label: 'Seu código interno de material'
  }
];

const ColumnsDataSequence = (props: ColumnsDataSequenceProps) => {
  return (
    <ColumnsDataContainer>
      {props.columns.map((el) => (
        <InputWrapper key={el.title}>
          <TextField
            label={el.title}
            maxLength={el.maxLength ?? 2}
            name={el.title}
            // value={el.value}
            value={props.inputsValues[el.type]}
            {...(!!el.type && {
              onChange: (e) => props.onInputChange(e, el.type)
            })}
            {...(el.helperText && {
              helperText: el.helperText,
              helperTextTooltipPlacement: 'bottomLeft'
            })}
          />
        </InputWrapper>
      ))}
    </ColumnsDataContainer>
  );
};

const SpreadsheetConfigModal: React.FC<SpreadsheetConfigModalProps> = ({
  canShowModal,
  setCanShowModal,
  onConfirmClick,
  orderEntryPreference
}) => {
  if (!canShowModal || !orderEntryPreference) {
    return null;
  }

  const getColumnPosition = (field: ESpreadsheetField): string => {
    const item = orderEntryPreference.spreadsheetColumns.find(
      (val) => val.field === field
    );
    return item ? item.position : '';
  };

  const [codeType, setCodeType] = useState<ECodeType>(
    orderEntryPreference.codeType
  );

  const [spreadsheetColumns, setSpreadsheetColumns] = useState<
    ESpreadsheetField[]
  >(
    orderEntryPreference.spreadsheetColumns
      .filter((col) => col.position !== '')
      .map((col) => col.field)
  );

  const [inputsValues, setInputsValues] = useState<Record<string, string>>({
    SKU: getColumnPosition(ESpreadsheetField.SKU),
    CLIENT_SKU: getColumnPosition(ESpreadsheetField.CLIENT_SKU),
    SKU_QUANTITY: getColumnPosition(ESpreadsheetField.QUANTITY),
    SKU_MEASURE_UNITY: getColumnPosition(ESpreadsheetField.UNITY),
    DESIRED_DATE: getColumnPosition(ESpreadsheetField.DESIRED_DATE),
    CUSTOMER_PURCHASE_ITEM: getColumnPosition(
      ESpreadsheetField.CUSTOMER_PURCHASE_ITEM
    )
  });

  const [startValuesRow, setStartValuesRow] = useState(
    orderEntryPreference.spreadsheetLine
  );

  const dispatch = useDispatch();

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const validationRegex = new RegExp(/^[A-z0-9]+$/);
    const typedText = event.target.value as string;
    if (validationRegex.test(typedText) || typedText === '') {
      return setInputsValues((prev) => ({
        ...prev,
        [type]: typedText.toUpperCase()
      }));
    }

    return event.preventDefault();
  };

  const hasErrorInAnyAdditionalInfo = (): boolean => {
    return (
      (codeType === ECodeType.SKU && inputsValues.SKU === '') ||
      (codeType === ECodeType.CLIENT_SKU && inputsValues.CLIENT_SKU === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.QUANTITY) &&
        inputsValues.SKU_QUANTITY === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.UNITY) &&
        inputsValues.SKU_MEASURE_UNITY === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.DESIRED_DATE) &&
        inputsValues.DESIRED_DATE === '') ||
      (spreadsheetColumns.includes(ESpreadsheetField.CUSTOMER_PURCHASE_ITEM) &&
        inputsValues.CUSTOMER_PURCHASE_ITEM === '')
    );
  };

  const onStartValuesLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbersRegex = new RegExp(/^[0-9]{1,3}$/);
    const typedNumber = e.target.value;
    if (onlyNumbersRegex.test(typedNumber) || typedNumber === '') {
      setStartValuesRow(Number(typedNumber));
    } else {
      e.preventDefault();
    }
  };

  const handleSelectChips = (selectedChip: string[]) => {
    if (selectedChip[0] === ECodeType.SKU) {
      return setCodeType(ECodeType.SKU);
    }

    if (selectedChip[0] === ECodeType.CLIENT_SKU) {
      return setCodeType(ECodeType.CLIENT_SKU);
    }

    return codeType;
  };

  const hasErrorInStartValuesLine = (): boolean => {
    return !startValuesRow || startValuesRow < 1;
  };

  const onConfirmConfig = () => {
    let hasConfigChanged = false;
    const checkboxUnchecked = getCheckboxInformation.filter(
      (el) => !el.checked
    );

    const newInputsValues = {
      SKU: checkboxUnchecked.find((el) => el.type === 'SKU')
        ? ''
        : inputsValues.SKU,
      CLIENT_SKU: checkboxUnchecked.find((el) => el.type === 'CLIENT_SKU')
        ? ''
        : inputsValues.CLIENT_SKU,
      SKU_QUANTITY: checkboxUnchecked.find((el) => el.type === 'SKU_QUANTITY')
        ? ''
        : inputsValues.SKU_QUANTITY,
      SKU_MEASURE_UNITY: checkboxUnchecked.find(
        (el) => el.type === 'SKU_MEASURE_UNITY'
      )
        ? ''
        : inputsValues.SKU_MEASURE_UNITY,
      DESIRED_DATE: checkboxUnchecked.find((el) => el.type === 'DESIRED_DATE')
        ? ''
        : inputsValues.DESIRED_DATE,
      CUSTOMER_PURCHASE_ITEM: checkboxUnchecked.find(
        (el) => el.type === 'CUSTOMER_PURCHASE_ITEM'
      )
        ? ''
        : inputsValues.CUSTOMER_PURCHASE_ITEM
    };

    orderEntryPreference.spreadsheetColumns.forEach((column) => {
      switch (column.field) {
        case ESpreadsheetField.SKU:
          if (newInputsValues.SKU !== column.position) {
            column.position = newInputsValues.SKU;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.CLIENT_SKU:
          if (newInputsValues.CLIENT_SKU !== column.position) {
            column.position = newInputsValues.CLIENT_SKU;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.QUANTITY:
          if (newInputsValues.SKU_QUANTITY !== column.position) {
            column.position = newInputsValues.SKU_QUANTITY;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.UNITY:
          if (newInputsValues.SKU_MEASURE_UNITY !== column.position) {
            column.position = newInputsValues.SKU_MEASURE_UNITY;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.DESIRED_DATE:
          if (newInputsValues.DESIRED_DATE !== column.position) {
            column.position = newInputsValues.DESIRED_DATE;
            hasConfigChanged = true;
          }
          break;
        case ESpreadsheetField.CUSTOMER_PURCHASE_ITEM:
          if (newInputsValues.CUSTOMER_PURCHASE_ITEM !== column.position) {
            column.position = newInputsValues.CUSTOMER_PURCHASE_ITEM;
            hasConfigChanged = true;
          }
          break;
        default:
          break;
      }
    });

    if (
      codeType !== orderEntryPreference.codeType ||
      startValuesRow !== orderEntryPreference.spreadsheetLine
    ) {
      orderEntryPreference.codeType = codeType;
      orderEntryPreference.spreadsheetLine = startValuesRow;
      hasConfigChanged = true;
    }
    onConfirmClick(orderEntryPreference, hasConfigChanged);
  };

  const getColumns = useCallback((): ColumnDef[] => {
    const columns: ColumnDef[] = [
      {
        idx: 'skuColumn',
        title: 'Código de material',
        type: 'SKU'
      },
      {
        idx: 'clientSkuColumn',
        title: 'Código interno',
        type: 'CLIENT_SKU'
      },
      {
        idx: 'customerPurchaseItemColumn',
        title: 'Nº da linha',
        type: 'CUSTOMER_PURCHASE_ITEM',
        maxLength: 10
        // typeNumber: true
      },
      {
        idx: 'quantityColumn',
        title: 'Quantidade',
        type: 'SKU_QUANTITY'
      },
      {
        idx: 'unityColumn',
        title: 'Unidade de medida',
        type: 'SKU_MEASURE_UNITY'
      },
      {
        idx: 'dateColumn',
        title: 'Data desejada',
        type: 'DESIRED_DATE',
        helperText:
          'A data desejada deve seguir o formato padrão do Brasil que consiste em dia, mês e ano separados por barras (DD/MM/AAAA).'
      }
    ];

    const hideColumns: string[] = [];

    if (codeType === ECodeType.CLIENT_SKU) {
      hideColumns.push('skuColumn'); // Material Gerdau
    } else {
      hideColumns.push('clientSkuColumn'); // Material cliente
    }

    !spreadsheetColumns.includes(ESpreadsheetField.QUANTITY) &&
      hideColumns.push('quantityColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.UNITY) &&
      hideColumns.push('unityColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.DESIRED_DATE) &&
      hideColumns.push('dateColumn');
    !spreadsheetColumns.includes(ESpreadsheetField.CUSTOMER_PURCHASE_ITEM) &&
      hideColumns.push('customerPurchaseItemColumn');

    return columns.filter((col) => !hideColumns.includes(col.idx));
  }, [codeType, spreadsheetColumns]);

  const getCheckboxInformation = [
    {
      label:
        codeType === ECodeType.CLIENT_SKU
          ? 'Código interno'
          : 'Código de material',
      checked: true,
      disabled: true,
      name: ESpreadsheetField.SKU,
      type: 'SKU'
    },
    {
      label: 'Nº da linha',
      checked: spreadsheetColumns.includes(
        ESpreadsheetField.CUSTOMER_PURCHASE_ITEM
      ),
      disabled: false,
      name: ESpreadsheetField.CUSTOMER_PURCHASE_ITEM,
      type: 'CUSTOMER_PURCHASE_ITEM'
    },
    {
      label: 'Quantidade',
      checked: spreadsheetColumns.includes(ESpreadsheetField.QUANTITY),
      disabled: false,
      name: ESpreadsheetField.QUANTITY,
      type: 'SKU_QUANTITY'
    },
    {
      label: 'Unidade de medida',
      checked: spreadsheetColumns.includes(ESpreadsheetField.UNITY),
      disabled: false,
      name: ESpreadsheetField.UNITY,
      type: 'SKU_MEASURE_UNITY'
    },
    {
      label: 'Data desejada',
      checked: spreadsheetColumns.includes(ESpreadsheetField.DESIRED_DATE),
      disabled: false,
      name: ESpreadsheetField.DESIRED_DATE,
      type: 'DESIRED_DATE'
    }
  ];

  const CheckboxInformation = () => {
    return (
      <CheckBoxContainer>
        {getCheckboxInformation.map((el) => (
          <Checkbox
            key={el.label}
            label={el.label}
            checked={el.checked}
            disabled={el.disabled}
            name={el.name}
            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
              handleCheckBox(e, el.name, el.type)
            }
          />
        ))}
      </CheckBoxContainer>
    );
  };

  const handleCheckBox = (
    event: ChangeEvent<HTMLInputElement>,
    itemColumn: ESpreadsheetField,
    type: string
  ): void => {
    if (!event.target.checked) {
      // setInputsValues((prev) => ({ ...prev, [type]: '' }));
      setSpreadsheetColumns(
        spreadsheetColumns.filter((item) => item !== event.target.name)
      );
    } else {
      const columns = [...spreadsheetColumns, itemColumn];
      setSpreadsheetColumns(columns);
    }
  };

  const prepareAndSendTagging = (itemName: 'aplicar_alteracoes' | 'voltar') => {
    dispatch(
      sendTagging({
        screenName: 'comprar_por_planilha',
        etapa: 'configurar_planilha',
        botao_navegacao: `${itemName}`,
        event: 'click'
      })
    );
  };

  const onSaveChanges = () => {
    prepareAndSendTagging('aplicar_alteracoes');
    onConfirmConfig();
  };

  const hasEqualValues = (...values: string[]) => {
    const nonEmptyValues = values.filter((value) => value !== '');
    return nonEmptyValues.some(
      (value, index) => nonEmptyValues.indexOf(value) !== index
    );
  };

  const hasEqualValueColumns = () => {
    const isCustomerPurchaseItemChecked = getCheckboxInformation.find(
      ({ name }) => name === ESpreadsheetField.CUSTOMER_PURCHASE_ITEM
    )?.checked;
    if (isCustomerPurchaseItemChecked) {
      return hasEqualValues(
        codeType === ECodeType.CLIENT_SKU
          ? inputsValues.CLIENT_SKU
          : inputsValues.SKU,
        inputsValues.CUSTOMER_PURCHASE_ITEM,
        inputsValues.SKU_MEASURE_UNITY,
        inputsValues.DESIRED_DATE,
        inputsValues.SKU_QUANTITY
      );
    }
    return hasEqualValues(
      codeType === ECodeType.CLIENT_SKU
        ? inputsValues.CLIENT_SKU
        : inputsValues.SKU,
      inputsValues.SKU_MEASURE_UNITY,
      inputsValues.DESIRED_DATE,
      inputsValues.SKU_QUANTITY
    );
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'comprar_por_planilha',
        event: 'configurar_planilha',
        etapa: 'configurar_planilha'
      })
    );
  }, [spreadsheetColumns]);

  return (
    <ModalOverride>
      <Modal
        modalSize="lg"
        buttons={{
          left: {
            children: 'Voltar',
            name: 'Voltar',
            onClick: () => {
              prepareAndSendTagging('voltar');
              setCanShowModal(false);
            },
            type: 'button'
          },
          right: {
            children: 'Aplicar alterações',
            name: 'Aplicar alterações',
            onClick: onSaveChanges,
            type: 'button',
            disabled:
              hasErrorInAnyAdditionalInfo() ||
              hasErrorInStartValuesLine() ||
              hasEqualValueColumns()
          }
        }}
        onClose={() => setCanShowModal(false)}
        title="Configurar envio da planilha"
      >
        <ModalContent>
          <div>
            <Title>Selecione o código de material</Title>
            <Chips
              type={'choice'}
              selectedChipIds={[codeType]}
              handleSelect={handleSelectChips}
              chips={chipsData}
            />
          </div>

          <div>
            <Title>Selecione as informações adicionais</Title>
            <CheckboxInformation />
          </div>

          <div>
            <Title>
              Organize as informações adicionais de acordo com as colunas da sua
              planilha
            </Title>
            <ColumnsDataSequence
              inputsValues={inputsValues}
              columns={getColumns()}
              onInputChange={onInputChange}
            />
          </div>

          <TypeNumberLinesContainer>
            <Title>
              Digite a linha da sua planilha em que começam as informações
              adicionais
            </Title>
            <TextfieldWrapper>
              <TextField
                variant={hasErrorInStartValuesLine() ? 'danger' : undefined}
                value={startValuesRow}
                onChange={(e) => onStartValuesLineChange(e)}
                label={'Número da linha'}
                name={'Número da linha'}
                placeholder={'Número da linha'}
              />
            </TextfieldWrapper>
          </TypeNumberLinesContainer>
        </ModalContent>
      </Modal>
    </ModalOverride>
  );
};

export default SpreadsheetConfigModal;
